import { assumeRole } from '@/api';
import { authQueryKeys, orgQueryKeys } from '@/config/queryKeys';
import { ORGS_QUERY_GC_TIME, ORGS_QUERY_STALE_TIME } from '@/constants';
import { useRoles } from '@/hooks/useRoles';
import { ROLE_LABELS } from '@/lib/roles';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { useAppStore } from '@/stores/app';
/**
 * Hook to handle role switching with proper error handling
 */
import { useUnauthorizedReferrerStore } from '@/stores/unauthorizedReferrerStore';
import type { Org, Role, User } from '@/types';
import { isUnauthorizedError } from '@/types/errors';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { filterOrgs, isChildRoute, normalizePath } from './utils';

/**
 * Hook to fetch organizations with proper error handling and retry logic
 */
export function useOrganizations() {
  return useQuery<Org[]>({
    queryKey: orgQueryKeys.list(),
    queryFn: fetchOrganizations,
    staleTime: ORGS_QUERY_STALE_TIME,
    gcTime: ORGS_QUERY_GC_TIME,
    retry: (failureCount, error) => {
      // Don't retry on unauthorized errors
      if (isUnauthorizedError(error)) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to handle role assumption with optimistic updates
 */
export function useAssumeRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assumeRole,
    onSuccess: async (_data, variables) => {
      // Manual cache update for seamless switching
      queryClient.setQueryData<User | null>(authQueryKeys.me(), oldUser => {
        if (!oldUser) return null;
        return {
          ...oldUser,
          assumed_roles: {
            ...oldUser.assumed_roles,
            [variables.org_id]: variables.role,
          },
        };
      });

      await queryClient.invalidateQueries({ queryKey: authQueryKeys.me() });
      // A role change affects all org-scoped data, so invalidate every query keyed under
      // `orgs/<org_id>/` — instruments, runs, studies, samples, and future path-based queries.
      // The trailing slash prevents partial matches against sibling org IDs.
      await queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey.some(k => typeof k === 'string' && k.startsWith(`orgs/${variables.org_id}/`)),
      });
    },
  });
}

/**
 * Hook to handle organization switching logic including navigation
 */
export function useOrgSwitcher() {
  const { t: tOrgs } = useTranslation('orgs');
  const location = useLocation();
  const navigate = useNavigate();

  const [pendingOrg, setPendingOrg] = useState<Org | null>(null);

  const { setSelectedOrganization, setSelectedOrgId } = useAppStore(
    useShallow(state => ({
      setSelectedOrganization: state.setSelectedOrganization,
      setSelectedOrgId: state.setSelectedOrgId,
    })),
  );

  // Commit the org switch after navigation has completed. Navigation is
  // awaited in handleOrgNavigation before setPendingOrg is called, so by
  // the time this effect fires the route has already changed.
  useEffect(() => {
    if (pendingOrg) {
      setSelectedOrganization(pendingOrg);
      setSelectedOrgId(pendingOrg.id);
      toast.success(tOrgs('org_switched_success', { name: pendingOrg.name }));
      setPendingOrg(null);
    }
  }, [pendingOrg, setSelectedOrganization, setSelectedOrgId, tOrgs]);

  const handleOrgNavigation = useCallback(
    async (org: Org) => {
      const path = normalizePath(location.pathname);

      if (isChildRoute('/studies', path)) {
        await navigate({ replace: true, to: '/studies' });
        setPendingOrg(org);
      } else if (isChildRoute('/instrument_runs', path)) {
        await navigate({ replace: true, to: '/instrument_runs' });
        setPendingOrg(org);
      } else if (isChildRoute('/samples', path)) {
        await navigate({ replace: true, to: '/samples' });
        setPendingOrg(org);
      } else {
        setSelectedOrganization(org);
        setSelectedOrgId(org.id);
        toast.success(tOrgs('org_switched_success', { name: org.name }));
      }
    },
    [location.pathname, navigate, setSelectedOrganization, setSelectedOrgId, tOrgs],
  );

  return { handleOrgNavigation };
}

export function useRoleSwitcher(selectedOrg: Org, onClose: () => void) {
  const { t: tOrgs } = useTranslation('orgs');
  const { assumedRolesByOrg } = useRoles();
  const { mutateAsync: performAssumeRole, isPending: isAssumingRole } = useAssumeRole();
  const { handleOrgNavigation } = useOrgSwitcher();
  const navigate = useNavigate();
  const router = useRouter();
  const { referrerPath, clearReferrerPath } = useUnauthorizedReferrerStore(
    useShallow(s => ({
      referrerPath: s.referrerPath,
      clearReferrerPath: s.clearReferrerPath,
    })),
  );

  const handleSwitchRole = useCallback(
    async (org: Org, role: Role) => {
      try {
        const isCurrentOrg = org.id === selectedOrg.id;
        const currentRole = assumedRolesByOrg[org.id];

        // 1. Assume the role
        if (currentRole !== role) {
          await performAssumeRole({ org_id: org.id, role });
          toast.success(tOrgs('org_selector_role_switch_success', { role: ROLE_LABELS[role] }));
        }

        // 2. If switching to a different org, handle navigation
        if (!isCurrentOrg) {
          handleOrgNavigation(org);
        }

        // 3. If there is a stored unauthorized referrer and we're on /unauthorized, redirect to it and clear
        if (referrerPath && router.state.location.pathname === '/unauthorized') {
          navigate({ to: referrerPath });
          clearReferrerPath();
        }

        // close popover
        onClose();
      } catch (e) {
        console.error('Failed to switch role', e);
        toast.error(tOrgs('org_selector_role_switch_failed'));
      }
    },
    [
      selectedOrg.id,
      assumedRolesByOrg,
      performAssumeRole,
      handleOrgNavigation,
      tOrgs,
      onClose,
      referrerPath,
      clearReferrerPath,
      navigate,
      router.state.location.pathname,
    ],
  );

  return { handleSwitchRole, isAssumingRole };
}

/**
 * Hook to handle copying organization ID to clipboard
 */
export function useCopyOrgId(orgId: string, onClose: () => void) {
  const { t: tOrgs } = useTranslation('orgs');

  return useCallback(async () => {
    try {
      await navigator.clipboard.writeText(orgId);
      toast.success(tOrgs('org_selector_org_id_copied'));
      onClose();
    } catch {
      toast.error(tOrgs('org_selector_org_id_copy_failed'));
    }
  }, [orgId, tOrgs, onClose]);
}

/**
 * Hook to get filtered organizations (excluding the selected one)
 */
export function useOtherOrganizations(organizations: Org[] | undefined, selectedOrgId: string) {
  return useMemo(() => organizations?.filter(org => org.id !== selectedOrgId) ?? [], [organizations, selectedOrgId]);
}

/**
 * Hook to manage client-side org search/filter state.
 */
export function useOrgSearch(orgs: Org[]) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => filterOrgs(orgs, query), [orgs, query]);

  return { query, setQuery, filtered };
}
